import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { deleteClient, getClientContacts } from '@/features/clients/api/clients';
import { useClients } from '@/features/clients/hooks/useClients';

type ClientColumn = 'name' | 'email' | 'phone' | 'created';
type SortDirection = 'asc' | 'desc';

const columnLabels: Record<ClientColumn, string> = {
  name: 'Client Name',
  email: 'Email',
  phone: 'Telephone',
  created: 'Created',
};

const initialColumnWidths: Record<ClientColumn, number> = {
  name: 250,
  email: 280,
  phone: 180,
  created: 170,
};

export function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') ?? '';
  const queryClient = useQueryClient();
  const [filterInput, setFilterInput] = useState(initialSearch);
  const [query, setQuery] = useState(initialSearch);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnMenu, setColumnMenu] = useState<ClientColumn>('name');
  const [sort, setSort] = useState<{ column: ClientColumn; direction: SortDirection }>({
    column: 'name',
    direction: 'asc',
  });
  const [columnOrder, setColumnOrder] = useState<ClientColumn[]>([
    'name',
    'email',
    'phone',
    'created',
  ]);
  const [columnWidths, setColumnWidths] =
    useState<Record<ClientColumn, number>>(initialColumnWidths);
  const [editingColumnWidth, setEditingColumnWidth] = useState(false);
  const [columnWidthInput, setColumnWidthInput] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const clientsQuery = useClients(query);
  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const contactQueries = useQueries({
    queries: clients.map((client) => ({
      queryKey: ['clients', client.id, 'contacts'],
      queryFn: () => getClientContacts(client.id),
      staleTime: 60_000,
    })),
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: number[]) => Promise.all(ids.map((id) => deleteClient(id))),
    onSuccess: async () => {
      setSelectedIds(new Set());
      setDeleteConfirmationOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const allSelected = clients.length > 0 && clients.every((client) => selectedIds.has(client.id));
  const someSelected = clients.some((client) => selectedIds.has(client.id));
  const selectedClientId = selectedIds.size === 1 ? [...selectedIds][0] : null;
  const clientRows = useMemo(() => {
    const rows = clients.map((client, index) => ({
      client,
      primaryContact: contactQueries[index]?.data?.[0],
    }));

    return rows.sort((left, right) => {
      const leftValue = sortableValue(left, sort.column);
      const rightValue = sortableValue(right, sort.column);
      const comparison = leftValue.localeCompare(rightValue, 'en-GB', {
        numeric: true,
        sensitivity: 'base',
      });
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [clients, contactQueries, sort]);

  const submitFilter = (event: FormEvent) => {
    event.preventDefault();
    setSelectedIds(new Set());
    setQuery(filterInput.trim());
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(clients.map((client) => client.id)));
  };

  const toggleClient = (id: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openColumnMenu = (event: MouseEvent<HTMLElement>, column: ClientColumn) => {
    setColumnMenuAnchor(event.currentTarget);
    setColumnMenu(column);
    setEditingColumnWidth(false);
    setColumnWidthInput(String(columnWidths[column]));
  };

  const applySort = (direction: SortDirection) => {
    setSort({ column: columnMenu, direction });
    setColumnMenuAnchor(null);
  };

  const focusFilter = () => {
    setColumnMenuAnchor(null);
    requestAnimationFrame(() => filterInputRef.current?.focus());
  };

  const moveColumn = (offset: -1 | 1) => {
    setColumnOrder((current) => {
      const currentIndex = current.indexOf(columnMenu);
      const nextIndex = currentIndex + offset;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      const currentColumn = next[currentIndex];
      const adjacentColumn = next[nextIndex];
      if (!currentColumn || !adjacentColumn) return current;
      next[currentIndex] = adjacentColumn;
      next[nextIndex] = currentColumn;
      return next;
    });
    setColumnMenuAnchor(null);
  };

  const applyColumnWidth = () => {
    const width = Number(columnWidthInput);
    if (!Number.isFinite(width)) return;
    setColumnWidths((current) => ({
      ...current,
      [columnMenu]: Math.min(600, Math.max(96, Math.round(width))),
    }));
    setColumnMenuAnchor(null);
  };

  const columnHeading = (label: string, column: ClientColumn) => (
    <Button
      color="inherit"
      endIcon={
        <Stack direction="row" spacing={0} alignItems="center">
          {sort.column === column &&
            (sort.direction === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: '15px !important' }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: '15px !important' }} />
            ))}
          <KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />
        </Stack>
      }
      onClick={(event) => openColumnMenu(event, column)}
      sx={{ minWidth: 0, px: 0, color: 'text.primary', fontWeight: 600 }}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ p: 1, minWidth: 0 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.14)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minHeight: 44, px: 1 }}>
          <IconButton size="small" aria-label="Back" onClick={() => history.back()}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          {selectedIds.size === 0 && (
            <Button
              component={RouterLink}
              to="/clients/new"
              startIcon={<AddIcon />}
              sx={commandButtonSx}
            >
              New
            </Button>
          )}
          {selectedClientId !== null && (
            <Button
              component={RouterLink}
              to={`/clients/${selectedClientId}`}
              startIcon={<EditOutlinedIcon />}
              sx={commandButtonSx}
            >
              Edit
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button
              startIcon={<SmsOutlinedIcon />}
              onClick={() => void navigate(`/communications/sms/new?clients=${[...selectedIds].join(',')}`)}
              sx={commandButtonSx}
            >
              Send SMS
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteConfirmationOpen(true)}
              sx={deleteCommandButtonSx}
            >
              Delete
            </Button>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void clientsQuery.refetch()}
            sx={commandButtonSx}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.14)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1.5}
          sx={{ minHeight: 58, px: 2, py: 1 }}
        >
          <Typography component="h1" variant="h1" sx={{ mr: 'auto' }}>
            Active Clients
          </Typography>
          <Tooltip title="Additional filters will be added with the client detail workflow">
            <span>
              <IconButton aria-label="Filter options" disabled>
                <FilterAltOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Box component="form" onSubmit={submitFilter}>
            <TextField
              inputRef={filterInputRef}
              size="small"
              value={filterInput}
              onChange={(event) => setFilterInput(event.target.value)}
              placeholder="Filter by keyword"
              aria-label="Filter clients by keyword"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
                htmlInput: { 'aria-label': 'Filter clients by keyword' },
              }}
              sx={{ width: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { height: 36 } }}
            />
          </Box>
        </Stack>
        <Divider />

        {clientsQuery.isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void clientsQuery.refetch()}>
                Retry
              </Button>
            }
            sx={{ m: 2 }}
          >
            Clients could not be loaded from the existing PoolAppy service.
          </Alert>
        )}

        <TableContainer sx={{ minHeight: 360 }}>
          <Table
            size="small"
            aria-label="Clients"
            sx={{
              minWidth: 48 + columnOrder.reduce((total, column) => total + columnWidths[column], 0),
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                height: 44,
                py: 1.1,
                borderBottom: '1px solid #e1dfdd',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'Select all clients' }}
                  />
                </TableCell>
                {columnOrder.map((column) => (
                  <TableCell key={column} sx={{ width: columnWidths[column] }}>
                    {columnHeading(columnLabels[column], column)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {clientsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ height: 220 }}>
                    <CircularProgress size={28} aria-label="Loading clients" />
                  </TableCell>
                </TableRow>
              )}
              {!clientsQuery.isLoading && !clientsQuery.isError && clients.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ height: 180, color: 'text.secondary' }}
                  >
                    {query ? 'No clients match this keyword.' : 'No clients to show.'}
                  </TableCell>
                </TableRow>
              )}
              {clientRows.map(({ client, primaryContact }) => {
                return (
                  <TableRow
                    key={client.id}
                    hover
                    selected={selectedIds.has(client.id)}
                    role="link"
                    tabIndex={0}
                    aria-label={`Open ${client.name}`}
                    onClick={() => void navigate(`/clients/${client.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        void navigate(`/clients/${client.id}`);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -2,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(client.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleClient(client.id)}
                        inputProps={{ 'aria-label': `Select ${client.name}` }}
                      />
                    </TableCell>
                    {columnOrder.map((column) => (
                      <TableCell key={column} sx={{ width: columnWidths[column] }}>
                        {column === 'name' && (
                          <Link
                            component={RouterLink}
                            to={`/clients/${client.id}`}
                            underline="none"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {client.name}
                          </Link>
                        )}
                        {column === 'email' &&
                          (primaryContact?.email ? (
                            <Link
                              href={`mailto:${primaryContact.email}`}
                              underline="none"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {primaryContact.email}
                            </Link>
                          ) : (
                            '—'
                          ))}
                        {column === 'phone' &&
                          (primaryContact?.phone ?? primaryContact?.homePhone ?? '—')}
                        {column === 'created' && formatDate(client.createdAt)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
          Rows: {clients.length}
        </Typography>
      </Paper>

      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
      >
        <MenuItem onClick={() => applySort('asc')}>
          <ListItemIcon>
            {sort.column === columnMenu && sort.direction === 'asc' ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </ListItemIcon>
          A to Z
        </MenuItem>
        <MenuItem onClick={() => applySort('desc')}>
          <ListItemIcon>
            {sort.column === columnMenu && sort.direction === 'desc' ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </ListItemIcon>
          Z to A
        </MenuItem>
        <Divider />
        <MenuItem onClick={focusFilter}>
          <ListItemIcon>
            <FilterAltOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Filter by
        </MenuItem>
        <Divider />
        {editingColumnWidth ? (
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              applyColumnWidth();
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 2, py: 1 }}
          >
            <TextField
              autoFocus
              label="Width"
              type="number"
              size="small"
              value={columnWidthInput}
              onChange={(event) => setColumnWidthInput(event.target.value)}
              slotProps={{ htmlInput: { min: 96, max: 600, step: 8 } }}
              sx={{ width: 100 }}
            />
            <Button type="submit" size="small">
              Apply
            </Button>
          </Box>
        ) : (
          <MenuItem onClick={() => setEditingColumnWidth(true)}>
            <ListItemIcon>
              <ViewWeekOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Column width
          </MenuItem>
        )}
        <MenuItem
          disabled={columnOrder.indexOf(columnMenu) === 0}
          onClick={() => moveColumn(-1)}
        >
          <ListItemIcon>
            <ChevronLeftIcon fontSize="small" />
          </ListItemIcon>
          Move left
        </MenuItem>
        <MenuItem
          disabled={columnOrder.indexOf(columnMenu) === columnOrder.length - 1}
          onClick={() => moveColumn(1)}
        >
          <ListItemIcon>
            <ChevronRightIcon fontSize="small" />
          </ListItemIcon>
          Move right
        </MenuItem>
      </Menu>

      <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
        <DialogTitle>Delete selected clients?</DialogTitle>
        <DialogContent>
          This will permanently delete {selectedIds.size}{' '}
          {selectedIds.size === 1 ? 'client' : 'clients'} using the existing PoolAppy service.
        </DialogContent>
        {deleteMutation.isError && (
          <Alert severity="error" sx={{ mx: 3 }}>
            The selected clients could not be deleted. No further changes were attempted.
          </Alert>
        )}
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
          <Button
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate([...selectedIds])}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};

const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date);
}

function sortableValue(
  row: {
    client: { name: string; createdAt: string };
    primaryContact?: { email?: string | null; phone?: string | null; homePhone?: string | null };
  },
  column: ClientColumn,
): string {
  if (column === 'email') return row.primaryContact?.email ?? '';
  if (column === 'phone') return row.primaryContact?.phone ?? row.primaryContact?.homePhone ?? '';
  if (column === 'created') return row.client.createdAt;
  return row.client.name;
}
